import { istDateKey } from '@/lib/ist'
import { getDb, type SupplyChainDB } from './db'
import { newId } from '@/lib/ids'

/**
 * Typed key/value store for device-level facts. Add new keys to MetaSchema — the type keeps
 * reads and writes honest.
 */

export type DeviceTeamKind = 'VILLAGE' | 'SITE'

/** What registration tells the phone (POST /devices by the office; read back by the phone). */
export interface DeviceRegistration {
  /** Server device id — goes in every sync envelope. */
  id: string
  device_uid: string
  /** Never-reused prefix for codes minted on this phone, e.g. "A1". */
  code_prefix: string
  team: {
    kind: DeviceTeamKind
    /** groups.id for a village team, locations.id of the site for a store team. */
    id: string
    name: string
    /** qr_villages.code (village) or site code — used in session codes. */
    code: string
  }
  status: 'ACTIVE' | 'REVOKED'
  registered_at: string
}

export interface MetaSchema {
  device_uid: string
  device: DeviceRegistration | null
  vault_key: CryptoKey | null
  /** Fingerprint of the server's key bytes: an unchanged key is not re-imported. */
  vault_key_id: string | null
  /** The key before the server last changed it: still opens rows sealed with it until resealed. */
  vault_key_prev: CryptoKey | null
  vault_key_prev_id: string | null
  roster_version: string | null
  roster_downloaded_at: number | null
  last_sync_at: number | null
  /** Server clock at last contact and our clock at that moment — for clock_skew_s. */
  server_clock: { server: number; local: number } | null
  pin_attempts: { failures: number; locked_until: number }
  /** Open farmers already backed up to the server: delivery_ulid → the draft's updated_at sent. */
  draft_backup_sent: Record<string, number>
}

export type MetaKey = keyof MetaSchema

export async function getMeta<K extends MetaKey>(
  key: K,
  db: SupplyChainDB = getDb(),
): Promise<MetaSchema[K] | undefined> {
  const row = await db.meta.get(key)
  return row?.value as MetaSchema[K] | undefined
}

export async function setMeta<K extends MetaKey>(
  key: K,
  value: MetaSchema[K],
  db: SupplyChainDB = getDb(),
): Promise<void> {
  await db.meta.put({ key, value })
}

/** This browser install's permanent id; shown on the setup screen for the office to register. */
export async function getOrCreateDeviceUid(db: SupplyChainDB = getDb()): Promise<string> {
  return db.transaction('rw', db.meta, async () => {
    const existing = await getMeta('device_uid', db)
    if (existing) return existing
    const uid = newId()
    await setMeta('device_uid', uid, db)
    return uid
  })
}

/**
 * Next value of a per-device counter for the IST date (session code `nn`, R17). Atomic within the
 * phone and never reused, even if the command that used it is voided.
 */
export async function nextDailyCounter(
  scope: string,
  at: Date | number = Date.now(),
  db: SupplyChainDB = getDb(),
): Promise<number> {
  const key = `counter:${scope}:${istDateKey(at)}`
  return db.transaction('rw', db.meta, async () => {
    const row = await db.meta.get(key)
    const next = (typeof row?.value === 'number' ? row.value : 0) + 1
    await db.meta.put({ key, value: next })
    return next
  })
}

/**
 * Seconds the phone clock was ahead (+) / behind (−) the server at the last sync. Wall-clock
 * elapsed time cannot be trusted across restarts, so this is the skew observed at last contact;
 * a clock changed by hand since then shows up at the next sync (F4.1: flag only, never blocks).
 */
export async function clockSkewSeconds(db: SupplyChainDB = getDb()): Promise<number | null> {
  const c = await getMeta('server_clock', db)
  if (!c) return null
  return Math.round((c.local - c.server) / 1000)
}
